// src/oura/components/newscan/OutletList.tsx
//
// The places on the Business step: outlets (Visit us), areas served (We go to
// them) or showrooms (Both, where a showroom is an outlet). One row per place: the flagship star, the area,
// the town its city-level questions name, where it came from (on the site,
// added by staff, or on a First AI check the client's form, the pitch or the
// quote) and a remove button. "Add outlet" opens one inline field:
// Enter adds and keeps the field open for the next one, Esc closes it.
//
// The first place is the flagship; the star on any other row makes it the
// flagship (it moves to the top). A place staff added that is not confirmed
// yet reads quieter until the Business step's card is answered.
//
// A name is edited in place (click it; Enter saves, Esc puts it back). A name
// one or two letters off a known town or a place the site names ("Subnag
// Jaya") gets a "Did you mean" line under its row. Remove offers Undo.
//
// The Town picker (TownPicker) offers the towns customers search with, never a
// state, and takes a typed town it does not list. A new place takes its own
// town when Oura knows it, else the business's city. "Kuala Lumpur" is saved
// as KL so city-wide questions never count one town twice. A place named after
// its own town (Subang Jaya in Subang Jaya) is asked about on the Business
// step, in a card above (AnchorTownCard).
//
// A single venue (`location`: a villa or venue, see singleVenue.ts) is one row
// with an "Also searched as" line for the other area names customers use. Its
// add button asks "Has more than one location?" instead of "Add outlet".

import { PlusIcon, StarIcon, XIcon } from "lucide-react";
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { Tooltip } from "@component/tooltip";
import { useSnackbar } from "@hook/use-snackbar";
import type { ScanDraftOutlet } from "@service/aeo/scanDraftTypes";
import { cn } from "@oura/lib/cn";
import { FOCUS_RING, PillButton, TYPE } from "@oura/ui";
import { inputClass } from "@oura/onboarding/messageText";
import { Muted } from "@oura/components/newscan/Muted";
import { InlineText } from "@oura/components/newscan/InlineText";
import { TOWN_LIST, townForPlace, townSpelling, usableTown } from "@oura/newscan/towns";
import type { OutletIntent } from "@oura/newscan/outletIntent";
import { OURA_AREA_NAMES_ENABLED } from "@oura/newscan/flags";
import { otherAreaNames, withCleanAreaNames } from "@oura/newscan/singleVenue";
import { townSplit } from "@oura/newscan/recipeShape";
import { isSigningSource, itemSource } from "./itemSource";
import { TagInput } from "./TagInput";
import { TownPicker } from "./TownPicker";

export interface OutletListProps {
    outlets: ScanDraftOutlet[];
    onChange(next: ScanDraftOutlet[]): void;
    /** The fallback town (the business's city), given to a new place. */
    city: string;
    /** The flagship star: the first place takes the larger share of the location questions. */
    flagship?: boolean;
    /** "outlet", "area", "showroom": names the add button and the field. */
    noun: "outlet" | "area" | "showroom";
    disabled?: boolean;
    /** Places the site read named, for the "Did you mean" check on a typed name. */
    known?: string[];
    /**
     * Open the field with this place typed in ("Name the area" on a First AI
     * check card): what is added takes its town and its source.
     */
    intent?: OutletIntent | null;
    /** One venue known by several area names: the first row gets "Also searched as". */
    location?: boolean;
}

/** Every town the picker lists, for the "Did you mean" check on a typed name. */
const TOWNS = TOWN_LIST;

const COPY = {
    add: { outlet: "Add outlet", area: "Add area", showroom: "Add showroom" },
    field: { outlet: "New outlet", area: "New area served", showroom: "New showroom" },
    placeholder: { outlet: "Area, e.g. SS15", area: "Area, e.g. USJ", showroom: "Area, e.g. Jaya One" },
    fieldHint: "Enter adds it. Esc closes.",
    town: "Town",
    townOf: (name: string) => `Town for ${name}`,
    flagship: "Flagship",
    makeFlagship: (name: string) => `Make ${name} the flagship`,
    remove: (name: string) => `Remove ${name}`,
    duplicate: (name: string) => `${name} is already in the list.`,
    added: (name: string) => `Added ${name}`,
    removed: (name: string) => `Removed ${name}`,
    undo: "Undo",
    restored: (name: string) => `${name} is back`,
    nameOf: (name: string) => `Name of ${name}`,
    renamed: (name: string) => `Renamed to ${name}`,
    didYouMean: (name: string) => `Did you mean ${name}?`,
    pickName: (name: string) => `Use ${name}`,
    keep: (name: string) => `Keep ${name}`,
    flagshipTip: "Flagship outlet",
    nowFlagship: (name: string) => `${name} is the flagship`,
    empty: { outlet: "No outlets yet.", area: "No areas yet.", showroom: "No showrooms yet." },
    alsoSearched: "Also searched as",
    alsoSearchedOf: (name: string) => `Other area names for ${name}`,
    alsoSearchedPlaceholder: "Add an area name, press Enter",
    alsoSearchedHint: "Location questions are asked under every name. Every answer counts for this one location.",
    moreLocations: "Has more than one location?",
    areaTitle: (n: number) => `${n} area-wide questions`,
    areaWaiting: (names: string[]) => `Pick a town for ${names.join(", ")} to see where the area-wide questions go.`,
    areaHint: "Questions like \u201cbest clinic in ___?\u201d name these towns, by how many outlets each has. Set each Town to where customers search.",
    overlap: (short: string, long: string) =>
        `${short} and ${long} overlap. Their location questions will read almost the same. Keep both only if customers search them separately.`,
    removeOne: (name: string) => `Remove ${name}`,
} as const;

/**
 * Two place names where one sits inside the other ("Puchong" in "Bandar Puchong"), shorter first.
 * Only names Oura reads as places: "ABC Clinic" and "ABC Clinic Puchong" are two branches, not one area.
 */
function overlaps(outlets: ScanDraftOutlet[]): Array<[number, number]> {
    const counted = outlets
        .map((o, i) => ({ i, name: o.name.trim().replace(/\s+/g, " ") }))
        .filter(({ name }) => name && townForPlace(name))
        .map(({ i, name }) => ({ i, low: ` ${name.toLowerCase()} ` }));
    const pairs: Array<[number, number]> = [];
    counted.forEach((a) =>
        counted.forEach((b) => {
            if (a.i !== b.i && a.low.length < b.low.length && b.low.includes(a.low)) pairs.push([a.i, b.i]);
        }),
    );
    return pairs;
}

/** Edit distance with swaps of two neighbouring letters counted as 1 ("Subnag" to "Subang"). */
function editDistance(a: string, b: string): number {
    const d: number[][] = Array.from({ length: a.length + 1 }, (_, i) => Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)));
    for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
        }
    }
    return d[a.length][b.length];
}

/** The known place a typed name is one or two letters off, or null. Short names ("KL") need a closer hit. */
function nearMiss(name: string, candidates: string[]): string | null {
    const typed = name.trim().toLowerCase();
    let best: { name: string; d: number } | null = null;
    for (const c of candidates) {
        const k = c.trim().toLowerCase();
        if (!k || k === typed) continue;
        const d = editDistance(typed, k);
        if (d > 2 || d * 3 >= Math.min(typed.length, k.length)) continue;
        // Two letters off with a different first letter is another place, not a typo ("Puchong" is not "Kuching").
        if (d === 2 && typed[0] !== k[0]) continue;
        if (!best || d < best.d) best = { name: c.trim(), d };
    }
    return best?.name ?? null;
}

/** The place without staff's "no smaller area" answer, which belongs to its old name. */
function withoutAnswer(o: ScanDraftOutlet): ScanDraftOutlet {
    const rest = { ...o };
    delete rest.noSmallerArea;
    return rest;
}

/**
 * A renamed place is no longer what the site said: it reads "Added by you". It was
 * already on the list, so it stays counted (confirmed) and asks no new question.
 */
function renamedTo(o: ScanDraftOutlet, name: string, town: string | null): ScanDraftOutlet {
    const base = withoutAnswer(o);
    const fromSite = base.source === "site";
    // Renamed to one of its other area names: that name leaves the list, so it is never asked about twice.
    return withCleanAreaNames({ ...base, name, ...(town ? { town } : {}), ...(fromSite ? { source: "staff" as const, confirmed: true } : {}) });
}

const ICON_BUTTON = cn(
    "relative grid h-7 w-7 shrink-0 place-items-center rounded-full",
    "after:absolute after:-inset-2 after:content-[''] motion-safe:transition-colors disabled:cursor-not-allowed disabled:opacity-40",
    FOCUS_RING,
);

// The shared Select (src/components/select.tsx), as every other dropdown in the product
// (Jovi, 20 Oct: the native select read as a different product). A fixed column from sm up.
const TOWN_TRIGGER = cn("h-8 w-auto min-w-[7rem] gap-2 rounded-md bg-white text-gray-700 shadow-none max-sm:max-w-full max-sm:text-base sm:w-36 disabled:bg-gray-50", TYPE.tc);

export function OutletList({ outlets, onChange, city, flagship = true, noun, disabled, known = [], intent = null, location = false }: OutletListProps) {
    const snackbar = useSnackbar();
    // Undo runs seconds later, from the snackbar: it must see the list and the handler as they are then.
    const latest = useRef({ outlets, onChange });
    latest.current = { outlets, onChange };
    const [adding, setAdding] = useState(() => !!intent);
    /** The place's town and source while the field holds an intent's area. Dropped once added or closed. */
    const [fromIntent, setFromIntent] = useState<OutletIntent | null>(intent);
    /** Typed name (lower case) to the known place it is likely a typo of; dropped once answered. */
    const [hints, setHints] = useState<Record<string, string>>({});
    const [text, setText] = useState(() => intent?.name ?? "");
    const [error, setError] = useState<string | null>(null);
    const [said, setSaid] = useState("");
    const addWrap = useRef<HTMLDivElement>(null);
    const focusAdd = () => addWrap.current?.querySelector<HTMLElement>("button")?.focus();
    const listRef = useRef<HTMLUListElement>(null);
    const fieldId = useId();
    const akaId = `${fieldId}-aka`;
    const noteId = `${fieldId}-note`;

    const say = (line: string) => setSaid((was) => (was === line ? `${line}\u200b` : line));

    // Opened with an intent: the field is where the staffer is going, so it comes into view with the caret at the end.
    // Each distinct intent opens the field, also one that arrives after the list has mounted (a
    // Business-step decision setting "Name the area"); clearing the prop lets the same one come again (Jack, #4286).
    const fieldRef = useRef<HTMLInputElement>(null);
    const handledIntent = useRef<string | null>(null);
    const intentSignature = intent ? JSON.stringify([intent.name, intent.town, intent.source]) : null;
    useEffect(() => {
        if (!intent || !intentSignature) {
            handledIntent.current = null;
            return;
        }
        if (handledIntent.current === intentSignature) return;
        handledIntent.current = intentSignature;
        setAdding(true);
        setFromIntent(intent);
        setText(intent.name);
        setError(null);
        const t = window.setTimeout(() => {
            const el = fieldRef.current;
            if (!el) return;
            el.scrollIntoView({ block: "center" });
            el.focus({ preventScroll: true });
            el.setSelectionRange(el.value.length, el.value.length);
            // After the step's own arrival focus (its title, a frame after the step changes).
        }, 150);
        return () => window.clearTimeout(t);
        // The signature stands for the intent's contents.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intentSignature]);

    /** The business's city as a town ("Kuala Lumpur" is KL); "" when it is a state ("Selangor"), so staff pick the town. */
    const cityTown = usableTown(city);

    // A town saved in another spelling of a listed one ("Kuala Lumpur") is saved as listed (KL), once.
    useEffect(() => {
        if (disabled) return;
        if (!outlets.some((o) => o.town.trim() && townSpelling(o.town) !== o.town)) return;
        onChange(outlets.map((o) => (o.town.trim() && townSpelling(o.town) !== o.town ? { ...o, town: townSpelling(o.town) } : o)));
        // Only the towns decide whether anything needs saving.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled, outlets.map((o) => o.town).join("|")]);

    // A changed town can retire the anchor question, so the answer to it goes too.
    const setTown = (i: number, town: string) =>
        onChange(outlets.map((o, j) => (j !== i || o.town === town ? o : { ...withoutAnswer(o), town })));

    /** The town a typed name stands for ("Petaling Jaya" is PJ), or null. */
    const townOf = (name: string) => townForPlace(name);
    const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

    /** Note a likely typo of a known town or a place the site names. */
    const checkTypo = (name: string, list: ScanDraftOutlet[]) => {
        const sitePlaces = [...known, ...list.filter((o) => o.source === "site").map((o) => o.name)];
        const hit = nearMiss(name, [...TOWNS, ...sitePlaces]);
        setHints((was) => {
            const next = { ...was };
            delete next[name.toLowerCase()];
            if (hit) next[name.toLowerCase()] = hit;
            return next;
        });
    };
    const dropHint = (name: string) =>
        setHints((was) => {
            const next = { ...was };
            delete next[name.toLowerCase()];
            return next;
        });

    const rename = (i: number, raw: string) => {
        const was = outlets[i];
        const name = raw.trim().replace(/\s+/g, " ");
        if (!was || !name || name === was.name) return;
        if (outlets.some((o, j) => j !== i && sameName(o.name, name))) {
            say(COPY.duplicate(name));
            snackbar.showError(COPY.duplicate(name));
            return;
        }
        const town = townOf(name);
        // A new name is a new answer to "is it a town": the old "no smaller area" goes with the old name.
        const next = outlets.map((o, j) => (j === i ? renamedTo(o, name, town) : o));
        onChange(next);
        dropHint(was.name);
        checkTypo(name, next);
        say(COPY.renamed(name));
    };

    /** "Use Subang Jaya": the row takes the known name (and its town); a place already listed just drops the typo row. */
    const applySuggestion = (i: number, suggested: string) => {
        const was = outlets[i];
        if (!was) return;
        dropHint(was.name);
        if (outlets.some((o, j) => j !== i && sameName(o.name, suggested))) {
            onChange(outlets.filter((_, j) => j !== i));
            say(COPY.duplicate(suggested));
            return;
        }
        const town = townOf(suggested);
        onChange(outlets.map((o, j) => (j === i ? renamedTo(o, suggested, town) : o)));
        say(COPY.renamed(suggested));
    };

    const makeFlagship = (i: number) => {
        const picked = outlets[i];
        if (!picked) return;
        onChange([picked, ...outlets.filter((_, j) => j !== i)]);
        say(COPY.nowFlagship(picked.name));
        // The row moved to the top, where it has no star: keep focus on that row, on its Town.
        requestAnimationFrame(() => listRef.current?.querySelector<HTMLElement>("[data-outlet-row='0'] [data-town-trigger]")?.focus());
    };

    const remove = (i: number) => {
        const gone = outlets[i];
        if (!gone) return;
        onChange(outlets.filter((_, j) => j !== i));
        say(COPY.removed(gone.name));
        snackbar.showSuccess(COPY.removed(gone.name), undefined, 6000, {
            actionLabel: COPY.undo,
            onAction: () => {
                const { outlets: now, onChange: change } = latest.current;
                if (now.some((o) => sameName(o.name, gone.name))) return;
                const at = Math.min(i, now.length);
                change([...now.slice(0, at), gone, ...now.slice(at)]);
                say(COPY.restored(gone.name));
            },
        });
        // Focus stays in the list: the next row's remove button, else the add button.
        requestAnimationFrame(() => {
            const rows = listRef.current?.querySelectorAll<HTMLElement>("[data-outlet-remove]");
            const next = rows?.[Math.min(i, (rows?.length ?? 1) - 1)];
            if (next) next.focus();
            else focusAdd();
        });
    };

    const close = () => {
        setAdding(false);
        setText("");
        setError(null);
        setFromIntent(null);
        requestAnimationFrame(focusAdd);
    };

    const commit = (): boolean => {
        const name = text.trim().replace(/\s+/g, " ");
        if (!name) return false;
        if (outlets.some((o) => o.name.trim().toLowerCase() === name.toLowerCase())) {
            setError(COPY.duplicate(name));
            setText("");
            return false;
        }
        // A place named after a known town sits in that town ("Subang Jaya"); a sub-area Oura knows sits in
        // its town ("Bangsar" in KL). Only an unknown area falls back to the city: never to itself, which
        // makes its city-wide questions repeat its location ones.
        const ownTown = townForPlace(name);
        // From an intent ("Name the area"): the form's town stands for a sub-area Oura does not know,
        // and the place is the client's, so it never reads as "not on the website".
        const added: ScanDraftOutlet = fromIntent
            ? { name, town: ownTown || usableTown(fromIntent.town ?? "") || cityTown, source: fromIntent.source, confirmed: true }
            : { name, town: ownTown || cityTown, source: "staff" };
        const next: ScanDraftOutlet[] = [...outlets, added];
        setFromIntent(null);
        onChange(next);
        checkTypo(name, next);
        say(COPY.added(name));
        setText("");
        setError(null);
        return true;
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.nativeEvent.isComposing) return;
        if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            if (!text.trim()) close();
            else commit();
            return;
        }
        if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            close();
        }
    };

    /** The first place's other area names; its own name never joins them. */
    const setAreaNames = (names: string[]) => {
        const first = outlets[0];
        if (!first) return;
        onChange([withCleanAreaNames({ ...first, alsoSearchedAs: names }), ...outlets.slice(1)]);
    };

    const area = townSplit(outlets, city, !!location && !!outlets[0] && otherAreaNames(outlets[0]).length > 0);
    const overlapping = location ? [] : overlaps(outlets);

    // The signing's longer labels ("From the client's form") widen the column for every row, so the rows line up.
    const wideSource = outlets.some((o) => isSigningSource(o.source));

    return (
        <div className="min-w-0">
            {outlets.length === 0 ? (
                <Muted as="p" className="py-1.5">
                    {COPY.empty[noun]}
                </Muted>
            ) : (
                <ul ref={listRef} role="list" className="list-plain m-0 space-y-1 p-0">
                    {outlets.map((o, i) => {
                        const isFlagship = flagship && i === 0;
                        const from = itemSource(o.source);
                        const FromIcon = from.icon;
                        const pending = o.source === "staff" && o.confirmed !== true;
                        const hint = hints[o.name.toLowerCase()];
                        return (
                            <li
                                // By position: a rename must not remount the row and lose focus.
                                key={i}
                                data-outlet-row={i}
                                className="flex min-h-10 flex-wrap items-center gap-x-3 gap-y-1.5"
                            >
                                <span className="flex min-w-[9rem] flex-1 items-center gap-2">
                                    {flagship &&
                                        (isFlagship ? (
                                            <Tooltip content={COPY.flagshipTip} asChild>
                                                <span className="grid h-7 w-7 shrink-0 place-items-center">
                                                    <StarIcon aria-hidden size={14} className="fill-amber-400 text-amber-400" />
                                                    <span className="sr-only">{COPY.flagship}: </span>
                                                </span>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip content={COPY.makeFlagship(o.name)} asChild>
                                                <button
                                                    type="button"
                                                    data-outlet-star=""
                                                    disabled={disabled}
                                                    aria-label={COPY.makeFlagship(o.name)}
                                                    onClick={() => makeFlagship(i)}
                                                    className={cn(ICON_BUTTON, "text-gray-300 hover:bg-gray-100 hover:text-amber-500")}
                                                >
                                                    <StarIcon aria-hidden size={14} />
                                                </button>
                                            </Tooltip>
                                        ))}
                                    <span className="min-w-0 flex-1">
                                        <InlineText
                                            value={o.name}
                                            label={COPY.nameOf(o.name)}
                                            empty={o.name}
                                            disabled={disabled}
                                            strong={!pending}
                                            required
                                            onCommit={(v) => rename(i, v)}
                                        />
                                    </span>
                                </span>
                                {/* DOM order is the visual order: on a phone Town, source and Remove wrap to their own line. */}
                                {/* No star indent on a phone: the town needs the width ("Klang Valley" in full). */}
                                <span className="flex min-w-0 items-center gap-x-3 max-sm:w-full">
                                    <span className={cn(TYPE.tc, "flex min-w-0 items-center gap-2 text-gray-500 max-sm:shrink-0")}>
                                        <span aria-hidden className="max-sm:hidden">
                                            {COPY.town}
                                        </span>
                                        <TownPicker
                                            value={o.town}
                                            inUse={[cityTown, ...outlets.map((x) => x.town)]}
                                            label={COPY.townOf(o.name)}
                                            disabled={disabled}
                                            className={TOWN_TRIGGER}
                                            onChange={(t) => setTown(i, t)}
                                        />
                                    </span>
                                    <Muted
                                        tone="ink4"
                                        className={cn("inline-flex min-w-0 shrink items-center gap-1.5 sm:shrink-0", wideSource ? "sm:w-[10.5rem]" : "sm:w-[6.5rem]")}
                                    >
                                        <FromIcon aria-hidden size={12} className="shrink-0" />
                                        <span className="sm:truncate">{from.label}</span>
                                    </Muted>
                                    <button
                                        type="button"
                                        data-outlet-remove=""
                                        disabled={disabled}
                                        aria-label={COPY.remove(o.name)}
                                        title={COPY.remove(o.name)}
                                        onClick={() => remove(i)}
                                        className={cn(ICON_BUTTON, "text-gray-400 hover:bg-gray-100 hover:text-gray-700 max-sm:ml-auto")}
                                    >
                                        <XIcon aria-hidden size={14} />
                                    </button>
                                </span>
                                {i === 0 && OURA_AREA_NAMES_ENABLED && (location || otherAreaNames(o).length > 0) && (
                                    <span data-outlet-aka="" className={cn("w-full min-w-0 space-y-1", flagship && "sm:pl-9")}>
                                        <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                                            <span aria-hidden className={cn(TYPE.tc, "shrink-0 text-gray-500")}>
                                                {COPY.alsoSearched}
                                            </span>
                                            <TagInput
                                                id={akaId}
                                                ariaLabel={COPY.alsoSearchedOf(o.name)}
                                                describedBy={`${akaId}-hint`}
                                                values={otherAreaNames(o)}
                                                onChange={setAreaNames}
                                                placeholder={COPY.alsoSearchedPlaceholder}
                                                disabled={disabled}
                                                className="min-w-0 flex-1"
                                            />
                                        </span>
                                        <Muted as="p" id={`${akaId}-hint`} className="m-0">
                                            {COPY.alsoSearchedHint}
                                        </Muted>
                                    </span>
                                )}
                                {hint && (
                                    <span className={cn("flex w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-1", flagship && "pl-9")}>
                                        <span className={cn(TYPE.tc, "text-gray-600")}>{COPY.didYouMean(hint)}</span>
                                        <PillButton variant="text" disabled={disabled} onClick={() => applySuggestion(i, hint)}>
                                            {COPY.pickName(hint)}
                                        </PillButton>
                                        <PillButton variant="text" disabled={disabled} onClick={() => dropHint(o.name)}>
                                            {COPY.keep(o.name)}
                                        </PillButton>
                                    </span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            {adding ? (
                <div className="mt-2 max-w-sm">
                    <label htmlFor={fieldId} className="sr-only">
                        {COPY.field[noun]}
                    </label>
                    <input
                        ref={fieldRef}
                        // Opened for "Name the area": the step's arrival focus lands here, not on its title.
                        data-arrival-focus={fromIntent ? "" : undefined}
                        id={fieldId}
                        type="text"
                        // The field opens on a click on "Add outlet": it is where the staffer is going.
                        autoFocus
                        value={text}
                        disabled={disabled}
                        autoComplete="off"
                        enterKeyHint="done"
                        placeholder={COPY.placeholder[noun]}
                        aria-describedby={noteId}
                        aria-invalid={error ? true : undefined}
                        onChange={(e) => {
                            setText(e.target.value);
                            if (error) setError(null);
                        }}
                        onKeyDown={onKeyDown}
                        onBlur={() => {
                            // The form's area, as the field opened with it: the step moving focus to its title on
                            // arrival must not add it unread. Staff add it with Enter, or after changing it.
                            if (fromIntent && text === fromIntent.name) return;
                            // Leaving the field adds what is typed; an empty field just closes.
                            if (!text.trim()) {
                                setAdding(false);
                                setError(null);
                            } else commit();
                        }}
                        className={inputClass(error)}
                    />
                    <p
                        id={noteId}
                        role={error ? "alert" : undefined}
                        className={cn(TYPE.tc, "m-0 mt-1.5", error ? "font-medium text-rose-600" : "text-gray-500")}
                    >
                        {error ?? COPY.fieldHint}
                    </p>
                </div>
            ) : (
                <div ref={addWrap} data-outlets-focus="" className="mt-1.5">
                <PillButton
                    variant="text"
                    icon={PlusIcon}
                    disabled={disabled}
                    onClick={() => setAdding(true)}
                >
                    {location && outlets.length > 0 ? COPY.moreLocations : COPY.add[noun]}
                </PillButton>
                </div>
            )}

            {overlapping.map(([short, long]) => (
                <div
                    key={`${outlets[short].name}|${outlets[long].name}`}
                    data-outlet-overlap=""
                    className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl bg-amber-50 px-3 py-2"
                >
                    <span className={cn(TYPE.tc, "min-w-0 flex-1 text-gray-800")}>{COPY.overlap(outlets[short].name, outlets[long].name)}</span>
                    {!disabled && (
                        <PillButton variant="text" onClick={() => remove(short)}>
                            {COPY.removeOne(outlets[short].name)}
                        </PillButton>
                    )}
                </div>
            ))}

            {area && area.missing.length > 0 && (
                <p data-area-split="" className={cn(TYPE.tc, "m-0 mt-3 rounded-xl bg-gray-50 px-3 py-2.5 text-gray-700")}>
                    {COPY.areaWaiting(area.missing)}
                </p>
            )}
            {area && area.missing.length === 0 && (
                <div data-area-split="" className="mt-3 rounded-xl bg-gray-50 px-3 py-2.5">
                    <p className={cn(TYPE.tc, "m-0 font-medium text-gray-900")}>
                        {COPY.areaTitle(area.total)}
                        <span className="font-normal text-gray-700">
                            {area.split.map(([town, n]) => (
                                <span key={town}>
                                    {" \u00b7 "}
                                    {town} {n}
                                </span>
                            ))}
                        </span>
                    </p>
                    <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>{COPY.areaHint}</p>
                </div>
            )}

            <span aria-live="polite" className="sr-only">
                {said}
            </span>
        </div>
    );
}
